import { useEffect } from 'react';
import { request } from '@/lib/api';
import { findRegistryPage } from '@/lib/viewRegistry';
import { suggestionContext } from '@/config/nav-groups';
import { PREFILL_DESC_PARAM, PREFILL_TITLE_PARAM } from '@/lib/feedbackEscalation';
import { recentApiCalls, recentConsoleErrors } from '@/lib/feedback/telemetry';
import { browserOf, platformOf, type Where } from '@/lib/feedback/core';

/* SPEC-506 — the "technical details" a report carries, gathered when the panel opens.
 *
 * v1 sources: the URL (path, ?tab=, the other query params as the visible filters), the
 * view registry (page + tab names), a compact freshness snapshot (feature flags are stamped by the server),
 * the last failed/slow requests and console errors, and the browser. A page may also
 * register its own on-screen selections with `useFeedbackContext` (none do yet).
 *
 * NEVER read here: request/response bodies, tokens, localStorage, cookies, or page text.
 * The panel shows the reader exactly this object before they send it. */

const registered = new Map<string, Record<string, unknown>>();

/** A page registers its visible filters/selections while mounted. Values must be the
 *  selections themselves (a product code, a horizon) — never page text or data rows. */
export function useFeedbackContext(key: string, filters: Record<string, unknown>): void {
  const signature = JSON.stringify(filters);
  useEffect(() => {
    registered.set(key, JSON.parse(signature));
    return () => { registered.delete(key); };
  }, [key, signature]);
}

const OMIT_PARAMS = new Set(['tab', PREFILL_TITLE_PARAM, PREFILL_DESC_PARAM]);

export function currentWhere(): Where {
  const c = suggestionContext(window.location.pathname, window.location.search);
  return { page: c.page, tab: c.tab, url: c.url, entity: c.context };
}

export function pageLabel(pathname: string, tab: string | null): string | null {
  const page = findRegistryPage(pathname);
  if (!page) return null;
  const tabLabel = tab ? page.groups?.flatMap((g) => g.tabs).find((t) => t.key === tab)?.label : undefined;
  return tabLabel ? `${page.label} · ${tabLabel}` : page.label;
}

async function freshnessSnapshot(): Promise<Record<string, unknown>> {
  try {
    const f = await request<any>('/api/governance/freshness');
    const tables = f?.tables && typeof f.tables === 'object' ? f.tables : {};
    const stale = Object.entries(tables)
      .filter(([, t]: [string, any]) => t?.is_stale)
      .map(([name, t]: [string, any]) => ({ table: name, max_date: t?.max_date ?? null, days_stale: t?.days_stale ?? null }));
    return {
      pipeline_status: f?.pipeline?.status ?? null,
      pipeline_last_run: f?.pipeline?.last_run ?? null,
      stale_tables: stale.slice(0, 15),
      integrity_breaches: f?.sentinels?.breach_count ?? null,
    };
  } catch {
    return { unavailable: true };
  }
}

/** Everything the report will carry, in the allow-listed schema's shape (the payload builder
 *  then applies `cleanContext`, which drops anything that does not fit). No free text. */
export async function gatherFeedbackContext(): Promise<Record<string, unknown>> {
  const params = new URLSearchParams(window.location.search);
  const filters: Record<string, unknown> = {};
  params.forEach((v, k) => { if (!OMIT_PARAMS.has(k)) filters[k] = v; });
  // Registered on-screen selections join the flat filter map (same shape rules apply).
  registered.forEach((sel) => Object.assign(filters, sel));
  // Feature flags are NOT gathered: the server stamps the platform's own (fix loop 3).
  const freshness = await freshnessSnapshot();
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  return {
    filters,
    freshness,
    api_calls: recentApiCalls(),
    console_errors: recentConsoleErrors(),
    client: {
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      screen: `${window.screen.width}x${window.screen.height}`,
      pixel_ratio: window.devicePixelRatio,
      ...browserOf(navigator.userAgent),
      platform: platformOf(nav.userAgentData?.platform ?? navigator.platform ?? ''),
      language: navigator.language,
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      utc_offset_minutes: -new Date().getTimezoneOffset(),
    },
    captured_at: new Date().toISOString(),
  };
}
